/**
 * PawLink store-app detection.
 *
 * When PawLink runs inside a store-distributed app (Google Play Capacitor
 * shell, Trusted Web Activity, or an in-app WebView launched from a native
 * iOS wrapper) we MUST NOT surface any in-app checkout for a digital
 * subscription. Google Play's Payments policy and Apple's 3.1.1 both
 * require the store's own billing for digital goods, and Google Play
 * additionally forbids linking users out of the app to an external
 * payment page. Presenting Paddle checkout (or a "buy on the web" CTA)
 * inside the store-distributed app is a certain rejection.
 *
 * This helper returns true when the app is running in one of those
 * contexts, so every upgrade CTA can hide itself. The web version at
 * getpawlink.com continues to work unchanged.
 *
 * Detection rules (deliberately permissive — false positives are safe,
 * false negatives are the rejection risk):
 *
 *   1. Capacitor native platform — `window.Capacitor.isNativePlatform()`
 *      returns true whenever the JS runs inside our Android (or future
 *      iOS) Capacitor shell. Highest-confidence signal.
 *   2. Android TWA — `document.referrer.startsWith("android-app://")`
 *      or user agent contains our package hint.
 *   3. iOS UIWebView / WKWebView launched by a native app — iOS UA
 *      without the "Safari" token AND not launched as a PWA.
 *   4. Explicit override — `?store=1` query flag or
 *      `localStorage.pawlink_store_app === "1"` so the native wrapper
 *      or a QA build can force the mode.
 *
 * The subscription/premium backend, Paddle integration, database fields,
 * pricing configuration and entitlement logic are NOT altered — this is
 * purely a client-side visibility gate for purchase CTAs.
 */

const TWA_PACKAGE_HINT = "com.getpawlink.app";

export function detectStoreApp() {
    try {
        if (typeof window === "undefined") return false;

        // 1) Capacitor native shell — highest-confidence signal. Set by
        //    the Capacitor runtime before any React code runs.
        try {
            const cap = window.Capacitor;
            if (cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform()) {
                return true;
            }
        } catch { /* Capacitor absent — fine */ }

        // 2) Explicit overrides.
        const urlFlag = new URLSearchParams(window.location.search).get("store");
        if (urlFlag === "1") return true;

        try {
            if (window.localStorage.getItem("pawlink_store_app") === "1") return true;
        } catch { /* localStorage denied */ }

        // 3) Android TWA hints.
        const ref = document.referrer || "";
        if (ref.startsWith("android-app://")) return true;

        const ua = navigator.userAgent || "";
        if (ua.includes(TWA_PACKAGE_HINT)) return true;

        // 4) iOS in-app WebView heuristic (kept for future iOS wrapper).
        const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        if (isIOS) {
            const looksLikeSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPT|GSA/.test(ua);
            const isStandalonePWA = window.navigator.standalone === true;
            if (!looksLikeSafari && !isStandalonePWA) return true;
        }

        return false;
    } catch {
        return false;
    }
}

/** React hook — snapshot only, no listener (store mode does not change). */
export function useIsStoreApp() {
    return detectStoreApp();
}

/**
 * Client-side entitlement helper — GRANTS access.
 *
 * Returns true when the user should be able to USE a Premium-gated feature:
 *   - real Premium (`user.plan === "premium"`), OR
 *   - Early Access on the store-app build (Google Play Android launch).
 *
 * Used for the Early Access-included free set: QR downloads, printable
 * exports, share, digital passport actions. See EARLY_ACCESS_LAUNCH_REPORT
 * for the full free/premium split.
 *
 * IMPORTANT — this is a UI-only helper. Server-side `plan`, subscription
 * records, and billing infrastructure remain intact for the compliant
 * Android monetization path.
 */
export function useHasProAccess(user) {
    const isStoreApp = useIsStoreApp();
    const isRealPremium = user?.plan === "premium";
    return isRealPremium || isStoreApp;
}

/**
 * Client-side entitlement helper — BLOCKS access.
 *
 * Returns true when a Premium feature should be **intercepted** and the
 * "Premium is coming soon" modal shown instead of executing the action.
 * Used for the Premium-locked set on Android: Lost Mode activation, live
 * GPS tracking, PawLink Map advanced rescue, contacting nearby helpers,
 * and adding a second pet (backend 402).
 *
 * True only when ALL of:
 *   - running inside the store-app (Google Play)
 *   - user is NOT already Premium
 *
 * On the web this returns false — the existing Paddle upgrade flow keeps
 * working exactly as before.
 */
export function useShouldGatePremium(user) {
    const isStoreApp = useIsStoreApp();
    const isRealPremium = user?.plan === "premium";
    return isStoreApp && !isRealPremium;
}
